import { PS, T } from '../lib/theme';
import { formatClock } from '../lib/time';
import { occupancyLine } from '../lib/occupancy';
import type { SeriesOccurrenceConflictDetail } from '../lib/types';

interface Props {
  detail: SeriesOccurrenceConflictDetail;
  /**
   * What staff can change instead, named for the edit that was rejected: a
   * time shift can pick another `window`, a transfer another `table`.
   *
   * A prop rather than a branch on `detail.code`, deliberately. The API sends
   * one body shape from both endpoints precisely so one renderer covers both
   * (see the narrowing note in `api.ts`); re-introducing a per-code branch here
   * would mean a third endpoint reusing this payload silently gets the wrong
   * sentence. The caller knows which edit it asked for — it says so.
   */
  alternative?: 'window' | 'table';
}

/**
 * The 409 a rejected series-wide edit returns, rendered per colliding date
 * (givip/prospin-api#84's time shift, #85's table transfer — the API sends both
 * in one shape so one component covers both).
 *
 * Read-only, like `SlotConflictBanner`: the edit is all-or-nothing server-side,
 * so there is nothing partial to confirm here, and resolving a conflict means
 * going to the bookings grid for that date. `SeriesConflictsBanner` does offer
 * an inline cancel, but that banner covers *creating* a projection where
 * resolving in place is the only workable flow; an existing series can simply
 * be shifted somewhere else.
 */
export function SeriesOccurrenceConflictBanner({ detail, alternative = 'window' }: Props) {
  const conflicts = detail.conflicts ?? [];
  if (conflicts.length === 0) return null;

  return (
    <div
      style={{
        ...T.caption,
        padding: 12,
        background: PS.errorBg,
        border: `1px solid ${PS.errorBorder}`,
        borderRadius: 3,
        color: PS.errorText,
      }}
    >
      {/* The API authors this sentence per call site — don't second-guess it. */}
      <div style={{ ...T.subheading, color: PS.errorText, marginBottom: 8 }}>{detail.error}</div>
      <div style={{ ...T.caption, color: PS.errorText, marginBottom: 8 }}>
        Nothing was changed — the edit is all or nothing. Free{' '}
        {conflicts.length === 1 ? 'this date' : 'these dates'} or pick another {alternative}.
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {conflicts.map((c) => (
          <div key={c.date}>
            <div style={{ ...T.label, color: PS.errorText }}>
              {c.date} · {formatClock(c.start_time)}–{formatClock(c.end_time)}
            </div>
            <ul style={{ margin: '2px 0 0', paddingLeft: 18 }}>
              {/* The index, scoped to this date's own list: two occupants can
                  share a start time with no booking id between them (a closure
                  and a block), which a composite key would collide on. The list
                  is static — it never reorders, filters or grows — so the
                  index is a stable identity here. */}
              {c.occupied_by.map((o, i) => (
                <li key={i}>{occupancyLine(o)}</li>
              ))}
            </ul>
            <div style={{ ...T.caption, color: PS.errorText, marginTop: 2 }}>
              {c.next_free_time
                ? `Next free on this table: ${formatClock(c.next_free_time)}`
                : 'No free time left on this table that day.'}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
